import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, ListChecks, ChartLine } from "@phosphor-icons/react";
import SopEditorDialog from "@/components/SopEditorDialog";
import PlannerOverrideDialog from "@/components/PlannerOverrideDialog";
import { SkuActions, SkuEditDialog, SkuDuplicateDialog, SkuDeleteDialog } from "@/components/SkuCrudDialogs";
import { useAuth } from "@/context/AuthContext";

export default function SKUs() {
  const { user: currentUser } = useAuth();
  const [skus, setSkus] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [sopSku, setSopSku] = useState(null);
  const [plannerSku, setPlannerSku] = useState(null);
  const [editSku, setEditSku] = useState(null);
  const [viewSku, setViewSku] = useState(null);
  const [dupSku, setDupSku] = useState(null);
  const [delSku, setDelSku] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", category: "Paneer Sabji", variant: "Regular", net_weight_g: 50, shelf_life_days: 365, cost_per_unit: 100, labor_cost_per_unit: 5, machine_cost_per_unit: 3, electricity_cost_per_unit: 4, overhead_cost_per_unit: 2, selling_price: 299 });

  const load = async () => setSkus((await api.get("/skus", { params: { include_inactive: showInactive }})).data);
  useEffect(() => { load(); }, [showInactive]);

  const categories = Array.from(new Set(skus.map(s => s.category)));
  const filtered = skus.filter(s =>
    (cat === "all" || s.category === cat) &&
    (s.name.toLowerCase().includes(q.toLowerCase()) || s.code.toLowerCase().includes(q.toLowerCase()))
  );

  const create = async () => {
    try {
      await api.post("/skus", form);
      toast.success("SKU created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="skus-page">
      <PageHeader
        subtitle="Master Data"
        title="SKUs & Recipes (BOM)"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-sku-btn"><Plus size={16} className="mr-1.5"/>New SKU</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create SKU</DialogTitle><DialogDescription className="sr-only">Create SKU</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code</Label><Input value={form.code} onChange={e=>setForm({...form, code:e.target.value})} data-testid="sku-code"/></div>
                <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="sku-name"/></div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={v=>setForm({...form, category:v})}>
                    <SelectTrigger data-testid="sku-category"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["Paneer Sabji","Vegetable Sabji","Dal","Indian Rice","Indian Combos","Chinese","Italian","Thai","Soups","Breakfast","Chutneys","Bhakhari","Tea","Gravies","Fruit Pulps"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Variant</Label>
                  <Select value={form.variant} onValueChange={v=>setForm({...form, variant:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="Regular">Regular</SelectItem><SelectItem value="Jain">Jain</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Net Weight (g)</Label><Input type="number" value={form.net_weight_g} onChange={e=>setForm({...form, net_weight_g:parseFloat(e.target.value)})}/></div>
                <div><Label>Shelf Life (days)</Label><Input type="number" value={form.shelf_life_days} onChange={e=>setForm({...form, shelf_life_days:parseInt(e.target.value)})}/></div>
                <div className="col-span-2 border-t border-border pt-3 mt-1">
                  <div className="overline mb-2">Cost Breakdown (₹ per pouch)</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Material Cost</Label><Input type="number" value={form.cost_per_unit} onChange={e=>setForm({...form, cost_per_unit:parseFloat(e.target.value)||0})} data-testid="sku-material-cost"/></div>
                    <div><Label className="text-xs">Labor</Label><Input type="number" value={form.labor_cost_per_unit} onChange={e=>setForm({...form, labor_cost_per_unit:parseFloat(e.target.value)||0})} data-testid="sku-labor-cost"/></div>
                    <div><Label className="text-xs">Machine</Label><Input type="number" value={form.machine_cost_per_unit} onChange={e=>setForm({...form, machine_cost_per_unit:parseFloat(e.target.value)||0})} data-testid="sku-machine-cost"/></div>
                    <div><Label className="text-xs">Electricity</Label><Input type="number" value={form.electricity_cost_per_unit} onChange={e=>setForm({...form, electricity_cost_per_unit:parseFloat(e.target.value)||0})} data-testid="sku-elec-cost"/></div>
                    <div><Label className="text-xs">Overhead</Label><Input type="number" value={form.overhead_cost_per_unit} onChange={e=>setForm({...form, overhead_cost_per_unit:parseFloat(e.target.value)||0})}/></div>
                    <div><Label className="text-xs">Selling ₹</Label><Input type="number" value={form.selling_price} onChange={e=>setForm({...form, selling_price:parseFloat(e.target.value)||0})} data-testid="sku-selling"/></div>
                  </div>
                  {(() => {
                    const tc = (form.cost_per_unit||0)+(form.labor_cost_per_unit||0)+(form.machine_cost_per_unit||0)+(form.electricity_cost_per_unit||0)+(form.overhead_cost_per_unit||0);
                    const margin = form.selling_price - tc;
                    const mpct = form.selling_price ? ((margin/form.selling_price)*100).toFixed(1) : 0;
                    return (
                      <div className="mt-3 p-2 rounded-md bg-muted flex items-center justify-between text-xs" data-testid="sku-margin-preview">
                        <span>Total mfg cost <b className="mono">₹{tc.toFixed(2)}</b></span>
                        <span>Margin <b className={`mono ${margin>=0?"text-emerald-600":"text-red-600"}`}>₹{margin.toFixed(2)} ({mpct}%)</b></span>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="sku-save">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="shadow-none mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input className="pl-9" placeholder="Search SKU code or name…" value={q} onChange={e=>setQ(e.target.value)} data-testid="sku-search"/>
          </div>
          <Tabs value={cat} onValueChange={setCat}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="all">All ({skus.length})</TabsTrigger>
              {categories.slice(0,6).map(c => <TabsTrigger key={c} value={c}>{c}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap"
                 data-testid="sku-show-inactive-label">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
                   data-testid="sku-show-inactive"/>
            Show inactive / deleted
          </label>
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Variant</TableHead>
                <TableHead className="text-right">Net Wt</TableHead>
                <TableHead className="text-right">Dry Yield</TableHead>
                <TableHead className="text-right">Cost ₹</TableHead>
                <TableHead className="text-right">Margin</TableHead>
                <TableHead>Ingredients</TableHead>
                <TableHead className="text-right">SOP</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(s => (
                <TableRow key={s.id} className={s.status && s.status !== "active" ? "opacity-70" : ""} data-testid={`sku-row-${s.code}`}>
                  <TableCell className="mono font-semibold">
                    {s.code}
                    {s.status && s.status !== "active" && (
                      <Badge variant="secondary" className="ml-1.5 uppercase text-[9px]" data-testid={`sku-status-${s.code}`}>{s.status}</Badge>
                    )}
                  </TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell><Badge variant="secondary">{s.category}</Badge></TableCell>
                  <TableCell>{s.variant === "Jain" ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Jain</Badge> : <span className="text-muted-foreground text-xs">Regular</span>}</TableCell>
                  <TableCell className="text-right mono">{s.net_weight_g}g</TableCell>
                  <TableCell className="text-right mono">{s.dry_yield_pct}%</TableCell>
                  <TableCell className="text-right mono">₹{s.cost_per_unit}</TableCell>
                  <TableCell className="text-right mono text-xs">
                    {(() => {
                      const tc = (s.cost_per_unit||0)+(s.labor_cost_per_unit||0)+(s.machine_cost_per_unit||0)+(s.electricity_cost_per_unit||0)+(s.overhead_cost_per_unit||0);
                      const margin = (s.selling_price||0) - tc;
                      return s.selling_price ? <span className={margin>=0?"text-emerald-600":"text-red-600"}>₹{margin.toFixed(0)}</span> : <span className="text-muted-foreground">—</span>;
                    })()}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(s.ingredients||[]).length} items</TableCell>
                  <TableCell className="text-right">
                    {(() => {
                      const prep = s.sop?.preparation?.length || 0;
                      const cook = s.sop?.cooking?.length || 0;
                      const hasOverride = !!s.planner_overrides && Object.keys(s.planner_overrides).length > 0;
                      return (
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => setSopSku(s)}
                                  data-testid={`sku-sop-btn-${s.code}`}>
                            <ListChecks size={13} className="mr-1"/>
                            {prep + cook > 0 ? `${prep}+${cook} steps` : "Add SOP"}
                          </Button>
                          <Button size="sm" variant={hasOverride ? "default" : "outline"}
                                  onClick={() => setPlannerSku(s)}
                                  data-testid={`sku-planner-btn-${s.code}`}
                                  title="Planner overrides">
                            <ChartLine size={13} className="mr-1"/>
                            {hasOverride ? "Override" : "Planner"}
                          </Button>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <SkuActions
                      sku={s}
                      currentUser={currentUser}
                      onEdit={setEditSku}
                      onView={setViewSku}
                      onDuplicate={setDupSku}
                      onDelete={setDelSku}
                      onRestore={async (sku) => {
                        try { await api.post(`/skus/${sku.id}/restore`); toast.success("SKU restored successfully."); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || e.message); }
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SopEditorDialog open={!!sopSku} onOpenChange={(o) => !o && (setSopSku(null), load())} sku={sopSku}/>
      <PlannerOverrideDialog open={!!plannerSku} onOpenChange={(o) => !o && (setPlannerSku(null), load())} sku={plannerSku}/>
      <SkuEditDialog open={!!editSku} onOpenChange={(o) => !o && setEditSku(null)} sku={editSku} mode="edit" onSaved={load}/>
      <SkuEditDialog open={!!viewSku} onOpenChange={(o) => !o && setViewSku(null)} sku={viewSku} mode="view"/>
      <SkuDuplicateDialog open={!!dupSku} onOpenChange={(o) => !o && setDupSku(null)} sku={dupSku} onDone={load}/>
      <SkuDeleteDialog open={!!delSku} onOpenChange={(o) => !o && setDelSku(null)} sku={delSku} onDone={load}/>
    </div>
  );
}
